/// <reference types="google.maps" />

import React, { useEffect, useMemo, useState, useRef } from 'react';
import Box from '@mui/material/Box';
import {
	TextField,
	TextFieldProps,
	Autocomplete,
	AutocompleteProps,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import parse from 'autosuggest-highlight/parse';
import { debounce } from '@mui/material/utils';
import styles from './AddressFieldSet.module.scss';
import useErrorScrollContext from '../../hooks/useErrorScrollContext';

/** BCP-47 tag; same role as `languageCode` on Places REST Autocomplete (preferred English results). */
const PLACES_LANGUAGE_CODE = 'en';

/**
 * Maps JS `language` does not exclude regions; we drop Quebec predictions to match
 * {@link StateSelect} (no French locale yet).
 */
const isQuebecAutocompletePrediction = (
	prediction: google.maps.places.AutocompletePrediction
): boolean => {
	const termValues = ( prediction.terms ?? [] ).map( ( term ) =>
		term.value.trim().toUpperCase()
	);
	if ( termValues.includes( 'QC' ) ) {
		return true;
	}
	if ( termValues.some( ( v ) => v === 'QUEBEC' || v === 'QUÉBEC' ) ) {
		return true;
	}
	const secondary = prediction.structured_formatting?.secondary_text ?? '';
	const combined = `${ prediction.description }, ${ secondary }`;
	return (
		/,\s*QC\s*,/i.test( combined ) ||
		/,\s*Quebec\s*,/i.test( combined ) ||
		/,\s*Québec\s*,/i.test( combined )
	);
};

const filterPredictionsForCountry = (
	countryCode: string | undefined,
	results:
		| readonly google.maps.places.AutocompletePrediction[]
		| null
		| undefined
): readonly google.maps.places.AutocompletePrediction[] | null => {
	if ( !results ) {
		return results ?? null;
	}
	if ( countryCode?.toUpperCase() !== 'CA' ) {
		return results;
	}
	return results.filter( ( p ) => !isQuebecAutocompletePrediction( p ) );
};

export type GoogleAutoCompleteField = {
	id?: string;
	label: string;
	placeholder?: string;
	onChange: ( arg0: string ) => void;
	setPlace: ( place: google.maps.places.PlaceResult | null ) => void;
	value: string;
	className?: string;
	error: boolean;
	helperText?: string;
	onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
	country?: string;
	disabled?: boolean;
	textFieldSlotProps?: TextFieldProps['slotProps'];
	variant?: TextFieldProps['variant'];
} & Omit<
	AutocompleteProps<
		google.maps.places.AutocompletePrediction,
		false,
		true,
		true
	>,
	| 'id'
	| 'className'
	| 'disabled'
	| 'value'
	| 'options'
	| 'onChange'
	| 'onInputChange'
	| 'renderInput'
	| 'getOptionLabel'
	| 'renderOption'
>;

type AutocompletePredictionMatch = {
	offset: number;
	length: number;
};

const GoogleAutoCompleteField: React.FC<GoogleAutoCompleteField> = ( {
	id,
	label,
	placeholder,
	setPlace,
	onChange,
	value,
	className,
	error,
	helperText,
	onBlur,
	country = 'us',
	disabled,
	textFieldSlotProps,
	variant,
	...autocompleteProps
} ) => {
	const [ placeSelection, setPlaceSelection ] =
		useState<google.maps.places.AutocompletePrediction | null>( null );
	const [ placeOptions, setPlaceOptions ] = useState<
		readonly google.maps.places.AutocompletePrediction[] | []
	>( [] );
	const autocompleteService =
		useRef<google.maps.places.AutocompleteService | null>( null );
	const placeService = useRef<google.maps.places.PlacesService | null>( null );

	const ref = useErrorScrollContext( !!error );

	const placesRef = useRef<HTMLInputElement | null>( null );
	const fetch = useMemo(
		() =>
			debounce(
				(
					request: google.maps.places.AutocompletionRequest,
					callback: (
						results: readonly google.maps.places.AutocompletePrediction[] | null
					) => void
				) => {
					autocompleteService.current?.getPlacePredictions( request, callback );
				},
				400
			),
		[]
	);

	// Store this in a variable we can use as a dependency for the useEffect
	const googleMaps = window.google;

	useEffect( () => {
		if ( !placeService.current && googleMaps && placesRef.current ) {
			placeService.current = new googleMaps.maps.places.PlacesService(
				placesRef.current
			);
		}
	}, [ googleMaps ] );

	useEffect( () => {
		let active = true;

		if ( !autocompleteService.current && window.google ) {
			autocompleteService.current =
				new window.google.maps.places.AutocompleteService();
		}
		if ( !autocompleteService.current ) {
			return undefined;
		}
		if ( value === '' ) {
			setPlaceOptions( placeSelection ? [ placeSelection ] : [] );
			return undefined;
		}

		fetch(
			{
				input: value,
				componentRestrictions: { country: [ country ] },
				language: PLACES_LANGUAGE_CODE,
			},
			(
				results: readonly google.maps.places.AutocompletePrediction[] | null
			) => {
				if ( active ) {
					let newOptions: readonly google.maps.places.AutocompletePrediction[] =
						[];

					if ( placeSelection ) {
						newOptions = [ placeSelection ];
					}

					const filtered = filterPredictionsForCountry( country, results );

					if ( filtered ) {
						newOptions = [ ...newOptions, ...filtered ];
					}

					setPlaceOptions( newOptions );
				}
			}
		);

		return () => {
			active = false;
		};
	}, [
		placeSelection,
		value,
		fetch,
		country
	] );

	useEffect( () => {
		if ( placeService.current && placeSelection?.place_id ) {
			placeService.current.getDetails(
				{
					placeId: placeSelection.place_id,
					language: PLACES_LANGUAGE_CODE,
				},
				(
					result: google.maps.places.PlaceResult | null,
					status: google.maps.places.PlacesServiceStatus
				) => {
					if ( status !== google.maps.places.PlacesServiceStatus.OK ) {
						setPlace( null );
						return;
					}
					setPlace( result );
				}
			);
		}
	}, [ placeSelection, setPlace ] );

	return (
		<>
			<Autocomplete<
				google.maps.places.AutocompletePrediction,
				false,
				true,
				true
			>
				ref={ ref }
				id={ id }
				{ ...autocompleteProps }
				getOptionLabel={ ( option ) =>
					typeof option === 'string' ? option : option.description
				}
				className={ className }
				options={ placeOptions || [] }
				autoComplete
				freeSolo
				includeInputInList
				disableClearable={ true }
				filterSelectedOptions
				value={ value }
				noOptionsText='No locations'
				disabled={ disabled }
				onChange={ ( _, newValue ) => {
					if ( typeof newValue !== 'string' ) {
						setPlaceOptions(
							newValue ? [ newValue, ...placeOptions ] : placeOptions
						);
						setPlaceSelection( newValue );
					}
				} }
				onInputChange={ ( _, newInputValue ) => {
					onChange( newInputValue );
				} }
				renderInput={ ( params ) => (
					<TextField
						{ ...params }
						label={ label }
						fullWidth
						variant={ variant ? variant : 'outlined' }
						error={ error }
						helperText={ helperText }
						placeholder={ placeholder }
						slotProps={ {
							...params.slotProps,
							...textFieldSlotProps,
							htmlInput: {
								...params.slotProps?.htmlInput,
								...textFieldSlotProps?.htmlInput,
							},
						} }
						onBlur={ onBlur }
					/>
				) }
				renderOption={ ( { key, ...props }, option ) => {
					const matches =
						option.structured_formatting.main_text_matched_substrings || [];

					const parts = parse(
						option.structured_formatting.main_text,
						matches.map( ( match: AutocompletePredictionMatch ) => [ match.offset, match.offset + match.length, ] )
					);

					return (
						<li key={ key } { ...props }>
							<Box sx={ { width: '100%', wordWrap: 'break-word' } }>
								{ parts.map( ( part, index ) => (
									<Box
										key={ index }
										component='span'
										sx={ { fontWeight: part.highlight ? 'bold' : 'regular' } }
									>
										{ part.text }
									</Box>
								) ) }
								<Typography
									variant='body2'
									color='text.secondary'
									className='address-autoselect-option'
								>
									{ option.structured_formatting.secondary_text }
								</Typography>
							</Box>
						</li>
					);
				} }
			/>
			<TextField inputRef={ placesRef } className={ styles.hidden }></TextField>
		</>
	);
};

export default GoogleAutoCompleteField;
